import React, { useState, useEffect } from 'react';

const TOTAL_TRIALS = 50;
const RESPONSE_WINDOW = 1250;
const FIXATION_DURATION = 750;

const generateStimuli = () => {
  const goTrials = Math.floor(TOTAL_TRIALS * 0.7);
  const noGoTrials = TOTAL_TRIALS - goTrials;
  const trials = [];

  for (let i = 0; i < goTrials; i++) {
    trials.push({ type: 'Go', color: '🟢' });
  }
  for (let i = 0; i < noGoTrials; i++) {
    trials.push({ type: 'NoGo', color: '🔴' });
  }

  return trials.sort(() => Math.random() - 0.5);
};

export default function GoNoGo({ onComplete }) {
  const [phase, setPhase] = useState('instructions');
  const [countdown, setCountdown] = useState(3);
  const [stimuli] = useState(generateStimuli());
  const [trialIndex, setTrialIndex] = useState(0);
  const [trialPhase, setTrialPhase] = useState('fixation');
  const [startTime, setStartTime] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);

  const currentStimulus = stimuli[trialIndex];

  useEffect(() => {
    if (phase === 'countdown') {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        setPhase('task');
        setTrialPhase('fixation');
      }
    }
  }, [phase, countdown]);

  useEffect(() => {
    if (phase === 'task' && trialIndex < stimuli.length) {
      if (trialPhase === 'fixation') {
        const timer = setTimeout(() => {
          setStartTime(Date.now());
          setClicked(false);
          setTrialPhase('stimulus');
        }, FIXATION_DURATION);
        return () => clearTimeout(timer);
      }

      if (trialPhase === 'stimulus') {
        const timer = setTimeout(() => {
          recordResponse(false, null);
        }, RESPONSE_WINDOW);
        return () => clearTimeout(timer);
      }
    }
  }, [phase, trialIndex, trialPhase]);

  const recordResponse = (userClicked, clickTime) => {
    const rt = userClicked ? clickTime - startTime : null;
    const stim = stimuli[trialIndex];

    const correct =
      (stim.type === 'Go' && userClicked) ||
      (stim.type === 'NoGo' && !userClicked);

    const trialData = {
      trial: trialIndex + 1,
      stimulus: stim.type,
      userClicked,
      correct,
      reactionTime: rt,
    };

    const updatedLog = [...log, trialData];
    setLog(updatedLog);

    if (trialIndex + 1 < stimuli.length) {
      setTrialIndex((i) => i + 1);
      setTrialPhase('fixation');
    } else {
      setDone(true);
      setPhase('done');

      const correct = updatedLog.filter((r) => r.correct).length;
      const avgRT =
        updatedLog
          .filter((r) => r.reactionTime !== null)
          .reduce((sum, r) => sum + r.reactionTime, 0) /
        updatedLog.filter((r) => r.reactionTime !== null).length;

      if (onComplete) {
        onComplete({
          correct,
          totalTrials: TOTAL_TRIALS,
          avgRT: isNaN(avgRT) ? null : avgRT,
        });
      }
    }
  };

  const handleClick = () => {
    if (!clicked) {
      setClicked(true);
      recordResponse(true, Date.now());
    }
  };

  if (phase === 'done') {
    const correct = log.filter((r) => r.correct).length;
    const falseAlarms = log.filter((r) => r.stimulus === 'NoGo' && r.userClicked).length;
    const misses = log.filter((r) => r.stimulus === 'Go' && !r.userClicked).length;
    const avgRT =
      log
        .filter((r) => r.reactionTime !== null)
        .reduce((sum, r) => sum + r.reactionTime, 0) /
      log.filter((r) => r.reactionTime !== null).length;

    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Go/No-Go Task Complete</h2>
        <p>Correct: {correct} / {TOTAL_TRIALS}</p>
        <p>False Alarms (clicked on 🔴): {falseAlarms}</p>
        <p>Missed Go trials (no click on 🟢): {misses}</p>
        <p>Avg RT (Go trials): {Math.round(avgRT)} ms</p>
      </div>
    );
  }

  if (phase === 'instructions') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Welcome to the Go/No-Go Task</h2>
        <p>You’ll see a sequence of 50 shapes.</p>
        <p><strong>Click ONLY</strong> when you see a 🟢 green circle.</p>
        <p><strong>DO NOT click</strong> when you see a 🔴 red circle.</p>
        <button onClick={() => setPhase('countdown')}>Start</button>
      </div>
    );
  }

  if (phase === 'countdown') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Get Ready</h2>
        <p>Starting in... {countdown}</p>
      </div>
    );
  }

  if (phase === 'task' && trialPhase === 'fixation') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Trial {trialIndex + 1} / {TOTAL_TRIALS}</h2>
        <div style={{ fontSize: '5rem' }}>+</div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: '3rem' }}>
      <h2>Trial {trialIndex + 1} / {TOTAL_TRIALS}</h2>
      <div style={{ fontSize: '6rem' }}>{currentStimulus.color}</div>
      <p>Click the button ONLY if it's a green circle 🟢</p>
      <button onClick={handleClick} disabled={clicked}>
        Click Here
      </button>
    </div>
  );
}